// STK500v1 Web Serial Flashing Engine for Arduino Uno & Nano (ATmega328P)

const STK_OK = 0x10;
const STK_INSYNC = 0x14;
const CRC_EOP = 0x20;
const STK_GET_SYNC = 0x30;
const STK_SET_DEVICE = 0x42;
const STK_ENTER_PROGMODE = 0x50;
const STK_LEAVE_PROGMODE = 0x51;
const STK_LOAD_ADDRESS = 0x55;
const STK_PROG_PAGE = 0x64;

export class FuturonSTK500Loader {
  constructor() {
    this.port = null;
    this.reader = null;
    this.writer = null;
    this.connected = false;
    this.boardName = 'Arduino';
    this.pageSize = 128; // ATmega328P page size

    this.onLog = null;
    this.onProgress = null;
    this.onStatusChange = null;
  }

  log(msg, type = 'info') {
    console.log(`[STK500:${type}]`, msg);
    if (this.onLog) this.onLog(msg, type);
  }

  async pulseReset() {
    this.log('Pulsing DTR to reset Arduino bootloader...', 'info');
    await this.port.setSignals({ dataTerminalReady: false, requestToSend: false });
    await new Promise(r => setTimeout(r, 100));
    await this.port.setSignals({ dataTerminalReady: true, requestToSend: true });
    await new Promise(r => setTimeout(r, 50));
    await this.port.setSignals({ dataTerminalReady: false, requestToSend: false });
    await new Promise(r => setTimeout(r, 100));
  }

  async sendCmd(cmdBytes, timeoutMs = 1000) {
    if (!this.writer) {
      this.writer = this.port.writable.getWriter();
    }
    
    await this.writer.write(new Uint8Array(cmdBytes));
    this.writer.releaseLock();
    this.writer = null;

    // Read response
    return await this.readResponse(timeoutMs);
  }

  async readResponse(timeoutMs = 1000) {
    if (!this.reader) {
      this.reader = this.port.readable.getReader();
    }

    const buffer = [];
    const startTime = Date.now();

    try {
      while (Date.now() - startTime < timeoutMs) {
        const { value, done } = await this.reader.read();
        if (done) break;
        if (value) {
          for (let b of value) buffer.push(b);
          // STK500 response usually ends with STK_OK (0x10)
          if (buffer.length >= 2 && buffer[buffer.length - 1] === STK_OK) {
            break;
          }
        }
      }
    } finally {
      if (this.reader) {
        this.reader.releaseLock();
        this.reader = null;
      }
    }

    return buffer;
  }

  async getSync(attempts = 10) {
    for (let i = 0; i < attempts; i++) {
      const res = await this.sendCmd([STK_GET_SYNC, CRC_EOP], 300);
      if (res.length >= 2 && res[0] === STK_INSYNC && res[1] === STK_OK) {
        return true;
      }
      await new Promise(r => setTimeout(r, 50));
    }
    return false;
  }

  async connect(baudRate = 115200, boardType = 'uno', customPort = null) {
    if (!('serial' in navigator)) {
      throw new Error('Web Serial API is not supported in this browser.');
    }

    this.boardName = boardType === 'uno' ? 'Arduino Uno' : 'Arduino Nano';

    try {
      if (customPort) {
        this.port = customPort;
      } else {
        this.log(`Requesting ${this.boardName} serial port selection from user dialog...`, 'info');
        this.port = await navigator.serial.requestPort();
      }
      
      this.log(`Opening serial port at ${baudRate} baud...`, 'info');
      await this.port.open({ baudRate: parseInt(baudRate, 10) });

      // Hardware reset into bootloader
      await this.pulseReset();

      this.log('Attempting STK500v1 sync sequence with Optiboot bootloader...', 'info');
      const synced = await this.getSync(15);
      
      if (!synced) {
        throw new Error('STK500 sync failed. Ensure your Arduino is connected and try pressing RESET on the board.');
      }

      this.connected = true;
      const chipInfo = {
        chipName: `${this.boardName} (ATmega328P)`,
        mac: 'AVR-8BIT',
        flashSize: '32 KB',
        crystalFreq: '16 MHz',
        revision: 'Optiboot STK500v1'
      };

      this.log(`Successfully synced with ${chipInfo.chipName}!`, 'success');
      
      if (this.onStatusChange) {
        this.onStatusChange(true, chipInfo);
      }

      return chipInfo;
    } catch (err) {
      this.log(`Failed to connect to ${this.boardName}: ${err.message}`, 'error');
      await this.disconnect();
      throw err;
    }
  }

  async disconnect() {
    this.connected = false;
    if (this.reader) {
      try { await this.reader.cancel(); } catch (e) {}
    }
    if (this.port) {
      try { await this.port.close(); } catch (e) {}
    }
    this.port = null;
    this.reader = null;
    this.writer = null;

    if (this.onStatusChange) {
      this.onStatusChange(false, null);
    }
    this.log('Arduino serial port disconnected.', 'info');
  }

  async writeFlash(hexBuffer) {
    if (!this.connected || !this.port) {
      throw new Error('No Arduino board connected.');
    }

    const data = hexBuffer.data;
    const totalBytes = hexBuffer.size || data.length;

    if (totalBytes === 0) {
      throw new Error('Firmware HEX buffer is empty.');
    }

    try {
      this.log(`Entering ATmega328P programming mode...`, 'info');
      await this.sendCmd([STK_ENTER_PROGMODE, CRC_EOP]);

      const pageSize = this.pageSize;
      const totalPages = Math.ceil(totalBytes / pageSize);
      const startTime = Date.now();

      this.log(`Writing ${totalBytes} bytes across ${totalPages} flash pages...`, 'info');

      for (let page = 0; page < totalPages; page++) {
        const wordAddress = (page * pageSize) / 2; // AVR 16-bit word address
        const addrLow = wordAddress & 0xFF;
        const addrHigh = (wordAddress >> 8) & 0xFF;

        // Load word address
        const loadAddrRes = await this.sendCmd([STK_LOAD_ADDRESS, addrLow, addrHigh, CRC_EOP]);
        if (loadAddrRes[0] !== STK_INSYNC || loadAddrRes[1] !== STK_OK) {
          throw new Error(`Failed to set address at page ${page}`);
        }

        // Prepare page data packet: STK_PROG_PAGE, sizeHigh, sizeLow, 'F', [bytes], CRC_EOP
        const pageChunk = data.slice(page * pageSize, (page + 1) * pageSize);
        const actualChunkSize = pageChunk.length;

        const cmd = [
          STK_PROG_PAGE,
          0x00,
          actualChunkSize,
          0x46 // 'F' for Flash
        ];

        for (let b of pageChunk) cmd.push(b);
        cmd.push(CRC_EOP);

        const progRes = await this.sendCmd(cmd, 1500);
        if (progRes[0] !== STK_INSYNC || progRes[1] !== STK_OK) {
          throw new Error(`Failed to write flash page ${page}`);
        }

        const writtenBytes = Math.min((page + 1) * pageSize, totalBytes);
        const percentage = Math.round((writtenBytes / totalBytes) * 100);
        const elapsedSec = (Date.now() - startTime) / 1000;
        const speedKbps = elapsedSec > 0 ? ((writtenBytes / 1024) / elapsedSec).toFixed(1) : 0;

        if (this.onProgress) {
          this.onProgress(writtenBytes, totalBytes, percentage, speedKbps);
        }
      }

      this.log('Leaving STK500 programming mode...', 'info');
      await this.sendCmd([STK_LEAVE_PROGMODE, CRC_EOP]);

      this.log(`Arduino firmware uploaded successfully! Rebooting...`, 'success');

      // Pulse reset again to reboot application
      await this.pulseReset();

    } catch (err) {
      this.log(`Arduino flashing error: ${err.message}`, 'error');
      throw err;
    }
  }

  async resetDevice() {
    if (this.port) {
      await this.pulseReset();
      this.log('Arduino hardware reset performed.', 'info');
    }
  }
}
